/** /auth/backup-codes — generate one-time backup codes, displayed ONCE. */
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as mfaApi from '@/api/mfa';

import { resolveLoginDestination } from './returnTo';

export function BackupCodesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Forwarded by MFAEnrollPage after an invite's enrolment; the home page otherwise.
  const dest = resolveLoginDestination(searchParams, null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      const r = await mfaApi.regenerateBackupCodes();
      setCodes(r.codes);
    } catch (e) {
      setError(e instanceof ConsoleApiError ? e.message : 'Failed to generate codes.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 8 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Backup codes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Generate ten single-use codes you can fall back to if you lose access to your primary
        second factor. Each code can be used only once.
      </Typography>

      {!codes && (
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Button variant="contained" onClick={generate} disabled={busy}>
            Generate codes
          </Button>
          <Button onClick={() => navigate(dest)}>Skip for now</Button>
        </Stack>
      )}

      {codes && (
        <Stack spacing={2}>
          <Alert severity="warning">
            These codes are shown <strong>once</strong>. Save them somewhere safe — you won't see
            them again.
          </Alert>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
              {codes.map((c) => (
                <Box key={c} sx={{ fontFamily: 'monospace', fontSize: 18 }}>
                  {c}
                </Box>
              ))}
            </Box>
          </Paper>
          <Button variant="contained" onClick={() => navigate(dest)}>
            I've saved them
          </Button>
        </Stack>
      )}
    </Container>
  );
}
